import { times } from 'lodash'
import axios from 'axios'
import deepmerge from 'deepmerge'
import { format, addYears, differenceInYears, parseISO } from 'date-fns'

import COMMON from '@/common'
import {
  mapTitle,
  mapGender,
  mapMartialStatus,
  reverseMapTitle,
  reverseMapGender,
  reverseMapMartialStatus,
} from '@/utils/registry_mappings'
import {
  formatDisplayName,
  formatDate,
  parseDateString,
  formatAddress,
  parseDateStringToThisYear,
  getDateFnsLocale,
} from '@/utils/helpers'
import { CHURCH_REGISTRY_USER_DEFAULTS } from '@/common/defaults'

export const parseUser = (userLikeObject) => {
  const userDefaults = JSON.parse(JSON.stringify(CHURCH_REGISTRY_USER_DEFAULTS))
  let user = JSON.parse(JSON.stringify(userLikeObject))
  if (!Object.keys(user).length) {
    return user
  }

  // Ensure correct mappings for dynamic fields
  if (user.bio.title) {
    user.bio.title = mapTitle(user.bio.title)
  }
  if (user.bio.gender) {
    user.bio.gender = mapGender(user.bio.gender)
  }
  if (user.bio.marital_status) {
    user.bio.marital_status = mapMartialStatus(user.bio.marital_status)
  }

  // Set defaults for user granted permissions
  if (typeof user.granted_permissions.show_birthday_in_gemeindefenster === 'undefined') {
    user.granted_permissions.show_birthday_in_gemeindefenster =
      userDefaults.granted_permissions.show_birthday_in_gemeindefenster
  }

  const today = new Date()
  const physicalAddress = user.address[COMMON.ADDRESS_TYPE_INDEXES.PHYSICAL]
  const postalAddress = user.address[COMMON.ADDRESS_TYPE_INDEXES.POSTAL]
  const funeral = user.church_services[`${COMMON.CHURCH_SERVICE_INDEXES.FUNERAL}`]
  const stami = user.churches[COMMON.CHURCHES_INDEXES.STAMI]
  const background = user.churches[COMMON.CHURCHES_INDEXES.BACKGROUND]
  const wedding = user.church_services[`${COMMON.CHURCH_SERVICE_INDEXES.WEDDING}`]
  const confirmation = user.church_services[`${COMMON.CHURCH_SERVICE_INDEXES.CONFIRMATION}`]
  const baptism = user.church_services[`${COMMON.CHURCH_SERVICE_INDEXES.BAPTISM}`]
  const dedication = user.church_services[`${COMMON.CHURCH_SERVICE_INDEXES.DEDICATION}`]

  const birthday = user.bio.birthday ? parseDateString(user.bio.birthday.slice(0, 10)) : ''
  const birthdayThisYear = birthday ? parseDateString(parseDateStringToThisYear(user.bio.birthday.slice(0, 10))) : ''
  const dateJoined = stami.joined ? parseDateString(stami.joined.slice(0, 10)) : ''
  const dateJoinedVisitor = background.joined ? parseDateString(background.joined.slice(0, 10)) : ''
  const weddingDate = wedding.date ? parseDateString(wedding.date.slice(0, 10)) : ''
  const weddingYear = wedding.date ? wedding.date.slice(0, 4) : ''
  const weddingDateThisYear = weddingDate ? parseDateString(parseDateStringToThisYear(wedding.date.slice(0, 10))) : ''
  const weddingMagistrateDate = wedding.magistrate ? parseDateString(wedding.magistrate.slice(0, 10)) : ''
  const weddingMagistrateYear = wedding.magistrate ? wedding.magistrate.slice(0, 4) : ''
  const weddingMagistrateDateThisYear = weddingMagistrateDate
    ? parseDateString(parseDateStringToThisYear(wedding.magistrate.slice(0, 10)))
    : ''
  const confirmationDate = confirmation.date ? parseDateString(confirmation.date.slice(0, 10)) : ''
  const confirmationYear = confirmation.date ? confirmation.date.slice(0, 4) : ''
  const baptismDate = baptism.date ? parseDateString(baptism.date.slice(0, 10)) : ''
  const baptismYear = baptism.date ? baptism.date.slice(0, 4) : ''
  const dedicationDate = dedication.date ? parseDateString(dedication.date.slice(0, 10)) : ''
  const dedicationYear = dedication.date ? dedication.date.slice(0, 4) : ''
  const deathDate = user.bio.death_date ? parseDateString(user.bio.death_date.slice(0, 10)) : ''
  const deathDateYear = user.bio.death_date ? user.bio.death_date.slice(0, 4) : ''
  const deathDateThisYear = deathDate
    ? parseDateString(parseDateStringToThisYear(user.bio.death_date.slice(0, 10)))
    : ''
  const funeralDate = funeral.date ? parseDateString(funeral.date.slice(0, 10)) : ''

  let birthdayData = {}
  const weddingData = {}
  let funeralData = {}

  if (birthday) {
    const yearsSinceBirthdate = differenceInYears(birthdayThisYear, birthday)
    const _birthday = formatDate(birthday) // Formatted birth date to user date format
    const _birthday_this_year = formatDate(addYears(birthday, yearsSinceBirthdate)) // Birthday with this years' year instead of birth year
    const _birthday_this_year_date = addYears(birthday, yearsSinceBirthdate)
    const _age = differenceInYears(today, birthday) // Age currently
    const _age_on_birthday_this_year = yearsSinceBirthdate // Age on birthday (i.e. turning/turned how old this year?)
    const alreadyHadBirthday = _age === _age_on_birthday_this_year
    const _next_birthday = alreadyHadBirthday
      ? formatDate(addYears(birthday, yearsSinceBirthdate + 1))
      : _birthday_this_year
    const _next_birthday_date = alreadyHadBirthday
      ? addYears(birthday, yearsSinceBirthdate + 1)
      : addYears(birthday, yearsSinceBirthdate)
    const _age_on_next_birthday = alreadyHadBirthday ? _age + 1 : _age_on_birthday_this_year
    const _birthday_long = format(birthday, 'iiii, do MMMM yyyy', { locale: getDateFnsLocale() })
    const _next_birthday_long = format(
      addYears(birthday, alreadyHadBirthday ? yearsSinceBirthdate + 1 : yearsSinceBirthdate),
      'iiii, do MMMM yyyy',
      { locale: getDateFnsLocale() },
    )

    birthdayData = {
      _birthday,
      _birthday_long,
      _birthday_this_year,
      _birthday_this_year_date,
      _next_birthday,
      _next_birthday_date,
      _next_birthday_long,
      _age,
      _age_on_birthday_this_year,
      _age_on_next_birthday,
    }
  }

  if (weddingDate) {
    const yearsSinceWedding = differenceInYears(weddingDateThisYear, weddingDate)
    const _wedding_date = formatDate(weddingDate)
    const _wedding_date_this_year = formatDate(addYears(weddingDate, yearsSinceWedding))
    const _wedding_date_this_year_date = addYears(weddingDate, yearsSinceWedding)
    const _wedding_age = differenceInYears(today, weddingDate)
    const _wedding_age_this_year = yearsSinceWedding

    weddingData._wedding_date = _wedding_date
    weddingData._wedding_year = weddingYear
    weddingData._wedding_date_this_year = _wedding_date_this_year
    weddingData._wedding_date_this_year_date = _wedding_date_this_year_date
    weddingData._wedding_age = _wedding_age
    weddingData._wedding_age_this_year = _wedding_age_this_year
  }

  if (weddingMagistrateDate) {
    const yearsSinceWeddingMagistrate = differenceInYears(weddingMagistrateDateThisYear, weddingMagistrateDate)
    const _wedding_magistrate_date = formatDate(weddingMagistrateDate)
    const _wedding_magistrate_date_this_year = formatDate(addYears(weddingMagistrateDate, yearsSinceWeddingMagistrate))
    const _wedding_magistrate_date_this_year_date = addYears(weddingMagistrateDate, yearsSinceWeddingMagistrate)
    const _wedding_magistrate_age = differenceInYears(today, weddingMagistrateDate)
    const _wedding_magistrate_age_this_year = yearsSinceWeddingMagistrate

    weddingData._wedding_magistrate_date = _wedding_magistrate_date
    weddingData._wedding_magistrate_year = weddingMagistrateYear
    weddingData._wedding_magistrate_date_this_year = _wedding_magistrate_date_this_year
    weddingData._wedding_magistrate_date_this_year_date = _wedding_magistrate_date_this_year_date
    weddingData._wedding_magistrate_age = _wedding_magistrate_age
    weddingData._wedding_magistrate_age_this_year = _wedding_magistrate_age_this_year
  }

  if (deathDate) {
    const yearsSinceDeath = differenceInYears(deathDateThisYear, deathDate)
    const _death_date = formatDate(deathDate)
    const _death_date_this_year = formatDate(addYears(deathDate, yearsSinceDeath))
    const _death_date_this_year_date = addYears(deathDate, yearsSinceDeath)
    const _years_since_death = differenceInYears(today, deathDate)
    const _years_since_death_this_year = yearsSinceDeath

    funeralData = {
      _death_date,
      _death_date_year: deathDateYear,
      _death_date_this_year,
      _death_date_this_year_date,
      _years_since_death,
      _years_since_death_this_year,
      _funeral_date: funeralDate ? formatDate(funeralDate) : '',
    }
  }

  // Prep for display
  user = deepmerge(user, {
    _full_name: formatDisplayName({ name: user.bio.name, nickname: user.bio.nickname, surname: user.bio.surname }),
    ...birthdayData,
    ...weddingData,
    ...funeralData,
    _telephone: getContactDetail(user, COMMON.CONTACT_TYPES.TELEPHONE),
    _work_telephone: getContactDetail(user, COMMON.CONTACT_TYPES.WORK_TELEPHONE),
    _mobile: getContactDetail(user, COMMON.CONTACT_TYPES.MOBILE),
    _email: getContactDetail(user, COMMON.CONTACT_TYPES.EMAIL),
    _member_status: user.churches[COMMON.CHURCHES_INDEXES.STAMI].member,
    _address: formatAddress({
      address1: physicalAddress.line1,
      address2: physicalAddress.line2,
      suburb: physicalAddress.suburb,
      city: physicalAddress.city,
      country: physicalAddress.country,
      code: physicalAddress.postal_code,
    }),
    _address_short: formatAddress({
      address1: physicalAddress.line1,
      address2: physicalAddress.line2,
      suburb: physicalAddress.suburb,
      city: physicalAddress.city,
    }),
    _postal_address: formatAddress({
      address1: postalAddress.line1,
      address2: postalAddress.line2,
      suburb: postalAddress.suburb,
      city: postalAddress.city,
      country: postalAddress.country,
      code: postalAddress.postal_code,
    }),
    _date_joined: dateJoined ? formatDate(dateJoined) : '',
    _years_since_joined: dateJoined ? differenceInYears(today, dateJoined) : '', // Years since joined
    _date_joined_visitor: dateJoinedVisitor ? formatDate(dateJoinedVisitor) : '',
    _years_since_joined_visitor: dateJoinedVisitor ? differenceInYears(today, dateJoinedVisitor) : '', // Years since joined
    _confirmation_date: confirmationDate ? formatDate(confirmationDate) : '',
    _confirmation_year: confirmationYear,
    _baptism_date: baptismDate ? formatDate(baptismDate) : '',
    _baptism_year: baptismYear,
    _dedication_date: dedicationDate ? formatDate(dedicationDate) : '',
    _dedication_year: dedicationYear,
    _occupation: user.occupations[0].occupation,
    _employer: user.occupations[0].employer,
    _church_background: user.churches[COMMON.CHURCHES_INDEXES.BACKGROUND].church,
    _church_background_joined: user.churches[COMMON.CHURCHES_INDEXES.STAMI].joined,
    _church_background_joined_visitor: user.churches[COMMON.CHURCHES_INDEXES.BACKGROUND].joined,
    _church_background_member_notes: user.churches[COMMON.CHURCHES_INDEXES.STAMI].member_notes,
    _church_background_member_bible_verse: user.churches[COMMON.CHURCHES_INDEXES.STAMI].member_bible_verse,
    _dedication_date_raw: user.church_services[COMMON.CHURCH_SERVICE_INDEXES.DEDICATION].date,
    _dedication_place: user.church_services[COMMON.CHURCH_SERVICE_INDEXES.DEDICATION].place,
    _dedication_pastor: user.church_services[COMMON.CHURCH_SERVICE_INDEXES.DEDICATION].pastor,
    _dedication_verse: user.church_services[COMMON.CHURCH_SERVICE_INDEXES.DEDICATION].verse,
    _dedication_note: user.church_services[COMMON.CHURCH_SERVICE_INDEXES.DEDICATION].note,
    _confirmation_date_raw: user.church_services[COMMON.CHURCH_SERVICE_INDEXES.CONFIRMATION].date,
    _confirmation_place: user.church_services[COMMON.CHURCH_SERVICE_INDEXES.CONFIRMATION].place,
    _confirmation_pastor: user.church_services[COMMON.CHURCH_SERVICE_INDEXES.CONFIRMATION].pastor,
    _confirmation_verse: user.church_services[COMMON.CHURCH_SERVICE_INDEXES.CONFIRMATION].verse,
    _confirmation_note: user.church_services[COMMON.CHURCH_SERVICE_INDEXES.CONFIRMATION].note,
    _baptism_date_raw: user.church_services[COMMON.CHURCH_SERVICE_INDEXES.BAPTISM].date,
    _baptism_place: user.church_services[COMMON.CHURCH_SERVICE_INDEXES.BAPTISM].place,
    _baptism_pastor: user.church_services[COMMON.CHURCH_SERVICE_INDEXES.BAPTISM].pastor,
    _baptism_verse: user.church_services[COMMON.CHURCH_SERVICE_INDEXES.BAPTISM].verse,
    _baptism_note: user.church_services[COMMON.CHURCH_SERVICE_INDEXES.BAPTISM].note,
    _wedding_date_raw: user.church_services[COMMON.CHURCH_SERVICE_INDEXES.WEDDING].date,
    _wedding_place: user.church_services[COMMON.CHURCH_SERVICE_INDEXES.WEDDING].place,
    _wedding_pastor: user.church_services[COMMON.CHURCH_SERVICE_INDEXES.WEDDING].pastor,
    _wedding_verse: user.church_services[COMMON.CHURCH_SERVICE_INDEXES.WEDDING].verse,
    _wedding_note: user.church_services[COMMON.CHURCH_SERVICE_INDEXES.WEDDING].note,
    _funeral_date_raw: user.church_services[COMMON.CHURCH_SERVICE_INDEXES.FUNERAL].date,
    _funeral_place: user.church_services[COMMON.CHURCH_SERVICE_INDEXES.FUNERAL].place,
    _funeral_pastor: user.church_services[COMMON.CHURCH_SERVICE_INDEXES.FUNERAL].pastor,
    _funeral_verse: user.church_services[COMMON.CHURCH_SERVICE_INDEXES.FUNERAL].verse,
    _funeral_note: user.church_services[COMMON.CHURCH_SERVICE_INDEXES.FUNERAL].note,
  })

  return user
}

export const syncUser = (userLikeObject, stripServerFields = false) => {
  const user = JSON.parse(JSON.stringify(userLikeObject))
  if (!Object.keys(user).length) {
    return user
  }

  // Strip fields which the server will add
  if (stripServerFields) {
    delete user.id
  }

  if (!user.bio.birthday) {
    delete user.bio.birthday
  }

  if (!user.bio.death_date) {
    delete user.bio.death_date
  }

  if (user.contact.length) {
    user.contact = user.contact.map((contact) => {
      if (contact.id) {
        return contact
      }
      delete contact.id
      return contact
    })
  }

  if (user.address.length) {
    user.address = user.address.map((address) => {
      if (address.id) {
        return address
      }
      delete address.id
      return address
    })
  }

  if (user.occupations.length) {
    user.occupations = user.occupations.map((occupation) => {
      if (occupation.id) {
        return occupation
      }
      delete occupation.id
      return occupation
    })
  }

  if (user.churches.length) {
    user.churches = user.churches.map((church) => {
      if (church.joined === '') {
        delete church.joined
      }
      if (church.id) {
        return church
      }
      delete church.id
      return church
    })
  }

  if (user.church_services.length) {
    user.church_services = user.church_services.map((service) => {
      if (service.date === '') {
        delete service.date
      }
      if (service.id) {
        return service
      }
      delete service.id
      return service
    })
  }

  // Ensure correct mappings for dynamic fields
  if (user.bio.title) {
    user.bio.title = reverseMapTitle(user.bio.title)
  }
  if (user.bio.gender) {
    user.bio.gender = reverseMapGender(user.bio.gender)
  }
  if (user.bio.marital_status) {
    user.bio.marital_status = reverseMapMartialStatus(user.bio.marital_status)
  }

  // Strip display fields
  delete user._full_name
  delete user._birthday
  delete user._birthday_long
  delete user._birthday_this_year
  delete user._birthday_this_year_date
  delete user._next_birthday
  delete user._next_birthday_date
  delete user._next_birthday_long
  delete user._age
  delete user._age_on_birthday_this_year
  delete user._age_on_next_birthday
  delete user._telephone
  delete user._work_telephone
  delete user._mobile
  delete user._email
  delete user._member_status
  delete user._address
  delete user._address_short
  delete user._postal_address
  delete user._date_joined
  delete user._years_since_joined
  delete user._date_joined_visitor
  delete user._years_since_joined_visitor
  delete user._death_date
  delete user._death_date_year
  delete user._death_date_this_year
  delete user._death_date_this_year_date
  delete user._years_since_death
  delete user._years_since_death_this_year
  delete user._funeral_date
  delete user._wedding_date
  delete user._wedding_year
  delete user._wedding_date_this_year
  delete user._wedding_date_this_year_date
  delete user._wedding_age
  delete user._wedding_age_this_year
  delete user._wedding_magistrate_date
  delete user._wedding_magistrate_year
  delete user._wedding_magistrate_date_this_year
  delete user._wedding_magistrate_date_this_year_date
  delete user._wedding_magistrate_age
  delete user._wedding_magistrate_age_this_year
  delete user._confirmation_date
  delete user._confirmation_year
  delete user._baptism_date
  delete user._baptism_year
  delete user._dedication_date
  delete user._dedication_year

  delete user.__group
  delete user.__day
  delete user.__birthday

  return user
}

export const getBirthdayForYear = function(birthday, year) {
  return addYears(parseISO(birthday), year - parseInt(birthday.slice(0, 4)))
}

export const getContactDetail = function(user, contactType) {
  const contactDetail = user.contact.find(({ contact_type }) => contact_type === contactType)
  return contactDetail ? contactDetail.contact : ''
}

export const hasPostalAddress = function(user) {
  const postalAddressExists = user.address.length && user.address[COMMON.ADDRESS_TYPE_INDEXES.POSTAL]
  if (postalAddressExists) {
    const hasPostalAddressFields = Object.entries(user.address[COMMON.ADDRESS_TYPE_INDEXES.POSTAL]).some(
      ([key, value]) => key !== 'id' && value !== '',
    )
    return hasPostalAddressFields
  }
  return false
}

const hasDetails = function(user, key = '') {
  const details = user.church_services[`${key}`]
  return !!(details.date || details.magistrate || details.place || details.pastor || details.verse || details.note)
}

export const hasMemberDetails = function(user) {
  const details = user.churches[`${COMMON.CHURCHES_INDEXES.STAMI}`]
  return details.member_bible_verse || details.joined || details.member_notes
}

export const hasMemberNotes = function(user) {
  const details = user.churches[`${COMMON.CHURCHES_INDEXES.STAMI}`]
  return details.member_bible_verse || details.member_notes
}

export const hasWeddingDetails = function(user) {
  return hasDetails(user, COMMON.CHURCH_SERVICE_INDEXES.WEDDING)
}

export const hasConfirmationDetails = function(user) {
  return hasDetails(user, COMMON.CHURCH_SERVICE_INDEXES.CONFIRMATION)
}

export const hasBaptismDetails = function(user) {
  return hasDetails(user, COMMON.CHURCH_SERVICE_INDEXES.BAPTISM)
}

export const hasDedicationDetails = function(user) {
  return hasDetails(user, COMMON.CHURCH_SERVICE_INDEXES.DEDICATION)
}

export const hasFuneralDetails = function(user) {
  return !!user._death_date || hasDetails(user, COMMON.CHURCH_SERVICE_INDEXES.FUNERAL)
}

export const isDeceased = function(user) {
  return (
    user._death_date ||
    user.bio.marital_status === COMMON.MARITAL_STATUS_OPTIONS.DECEASED ||
    user._member_status === COMMON.MEMBER_STATUS_OPTIONS.DECEASED
  )
}

const _getUsersRaw = function(page = 0) {
  return new Promise((resolve, reject) => {
    axios
      .get(`${COMMON.API.CHURCH_REGISTRY}?page=${page}`)
      .then(({ data }) => resolve(data))
      .catch(reject)
  })
}

const getUsers = function() {
  return new Promise((resolve, reject) => {
    _getUsersRaw()
      .then((data) => {
        const users = data.users
        const additionalCalls =
          data.pagination.total_items === data.pagination.size
            ? 0
            : Math.floor(data.pagination.total_items / data.pagination.size)
        if (additionalCalls > 0) {
          const promises = []
          times(additionalCalls, (index) => promises.push(_getUsersRaw(index + 1)))
          Promise.all(promises)
            .then((additionalData) => {
              const additionalUsers = additionalData.map((additional) => additional.users).flat()
              const allUsers = [...users, ...additionalUsers]
              resolve(allUsers.map(parseUser))
            })
            .catch(reject)
        } else {
          resolve(data.users.map(parseUser))
        }
      })
      .catch(reject)
  })
}

const addUser = function(user) {
  return new Promise((resolve, reject) => {
    axios
      .post(COMMON.API.CHURCH_REGISTRY, syncUser(user, true))
      .then(({ data }) => resolve(data))
      .catch(reject)
  })
}

const updateUser = function(user = {}, updates = {}) {
  return new Promise((resolve, reject) => {
    axios
      .put(`${COMMON.API.CHURCH_REGISTRY}/${user.id}`, syncUser(deepmerge(user, updates)))
      .then(({ data }) => resolve(parseUser(data)))
      .catch(reject)
  })
}

const deleteUser = function(userId) {
  return new Promise((resolve, reject) => {
    axios
      .delete(`${COMMON.API.CHURCH_REGISTRY}/${userId}`)
      .then(resolve)
      .catch(reject)
  })
}

export default {
  getUsers,
  addUser,
  updateUser,
  deleteUser,
}
